import React, { useState } from 'react';
import { ArrowUpTrayIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const ImportAdhesions = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({
    createUsers: true,
    skipExisting: true,
    defaultPassword: 'Apiculture2024!'
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewData(null);
      setImportResult(null);
      setError(null);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/migration/preview-adhesions`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setPreviewData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la prévisualisation');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!previewData?.allRows) {
      setError('Aucune donnée à importer');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir importer ${previewData.validRows} adhésion(s) ?`)) {
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/migration/import-adhesions`,
        {
          rows: previewData.allRows,
          options
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setImportResult(response.data);
      setPreviewData(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreviewData(null);
    setImportResult(null);
    setError(null);
    // Reset file input
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Import d'adhésions</h1>
        <p className="mt-2 text-sm text-gray-600">
          Importez des adhésions depuis un fichier Excel (.xlsx)
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-800 mb-2">Colonnes reconnues automatiquement :</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-blue-700">
          <div>
            <strong className="block mb-1">Obligatoires :</strong>
            <span>email, mail, nom, organisme, année, annee</span>
          </div>
          <div>
            <strong className="block mb-1">Informations personnelles :</strong>
            <span>prénom, prenom, civilité, civilite, designation, type, type personne, raison sociale, société, date naissance</span>
          </div>
          <div>
            <strong className="block mb-1">Contact :</strong>
            <span>téléphone, telephone, tel, mobile, telephone mobile, adresse, rue, complément, complement adresse, code postal, codepostal, cp, ville</span>
          </div>
          <div>
            <strong className="block mb-1">Informations apicoles :</strong>
            <span>napi, numéro napi, amexa, numero amexa, ruches, nombre ruches, nb ruches, ruchers, nombre ruchers, emplacements, département, departement, commune, siret</span>
          </div>
          <div>
            <strong className="block mb-1">Adhésion :</strong>
            <span>montant, type paiement, typepaiement, paiement, status, statut, nouveau, est nouveau, nouvel adhérent, notes, note</span>
          </div>
        </div>
      </div>

      {/* Upload section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700 mb-2">
              Fichier Excel
            </label>
            <input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
          </div>
          <div className="flex gap-2 pt-6">
            <button
              onClick={handlePreview}
              disabled={!file || loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Analyse...
                </>
              ) : (
                <>
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  Prévisualiser
                </>
              )}
            </button>
            {(previewData || importResult) && (
              <button
                onClick={resetForm}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Preview section */}
      {previewData && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900">{previewData.totalRows}</div>
              <div className="text-sm text-gray-500">Lignes totales</div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{previewData.validRows}</div>
              <div className="text-sm text-gray-500">Lignes valides</div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-2xl font-bold text-red-600">{previewData.errors.length}</div>
              <div className="text-sm text-gray-500">Erreurs</div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{previewData.mappedColumns.length}</div>
              <div className="text-sm text-gray-500">Colonnes mappées</div>
            </div>
          </div>

          {/* Mapped columns */}
          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Colonnes détectées :</h3>
            <div className="flex flex-wrap gap-2">
              {previewData.mappedColumns.map((col, idx) => (
                <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  {col}
                </span>
              ))}
            </div>
            {previewData.unmappedColumns.length > 0 && (
              <>
                <h3 className="font-medium text-gray-900 mt-4 mb-2">Colonnes non reconnues :</h3>
                <div className="flex flex-wrap gap-2">
                  {previewData.unmappedColumns.map((col, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {col}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Errors */}
          {previewData.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-800 mb-2 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                Lignes avec erreurs ({previewData.errors.length})
              </h3>
              <div className="max-h-40 overflow-y-auto">
                {previewData.errors.map((err, idx) => (
                  <div key={idx} className="text-sm text-red-700 py-1">
                    <strong>Ligne {err.row}:</strong> {err.errors.join(', ')}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">Aperçu des données (10 premières lignes)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prénom</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Organisme</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Année</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ruches</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.preview.map((row, idx) => (
                    <tr key={idx} className={previewData.errors.some(e => e.row === row.rowNumber) ? 'bg-red-50' : ''}>
                      <td className="px-3 py-2 text-sm text-gray-500">{row.rowNumber}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.email || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.nom || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.prenom || '-'}</td>
                      <td className="px-3 py-2 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${row.organisme === 'SAR' ? 'bg-blue-100 text-blue-800' : row.organisme === 'AMAIR' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {row.organisme || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.annee || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.nombreRuches || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.montant ? `${row.montant}€` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Options */}
          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-4">Options d'import</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={options.createUsers}
                  onChange={(e) => setOptions({ ...options, createUsers: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Créer les utilisateurs s'ils n'existent pas</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={options.skipExisting}
                  onChange={(e) => setOptions({ ...options, skipExisting: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Ignorer les adhésions existantes (sinon mise à jour)</span>
              </label>
              {options.createUsers && (
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Mot de passe par défaut pour les nouveaux utilisateurs</label>
                  <input
                    type="text"
                    value={options.defaultPassword}
                    onChange={(e) => setOptions({ ...options, defaultPassword: e.target.value })}
                    className="w-64 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Import button */}
          <div className="flex justify-end">
            <button
              onClick={handleImport}
              disabled={importing || previewData.validRows === 0}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Importer {previewData.validRows} adhésion(s)
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
            Import terminé
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{importResult.results.created}</div>
              <div className="text-sm text-green-700">Créées</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{importResult.results.updated}</div>
              <div className="text-sm text-blue-700">Mises à jour</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-600">{importResult.results.skipped}</div>
              <div className="text-sm text-gray-700">Ignorées</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-600">{importResult.results.errors.length}</div>
              <div className="text-sm text-red-700">Erreurs</div>
            </div>
          </div>

          {importResult.results.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-red-800 mb-2">Erreurs d'import :</h4>
              <div className="max-h-40 overflow-y-auto">
                {importResult.results.errors.map((err, idx) => (
                  <div key={idx} className="text-sm text-red-700 py-1">
                    <strong>Ligne {err.row} ({err.email}):</strong> {err.error}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center">
            <button
              onClick={resetForm}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Nouvel import
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportAdhesions;
